import { Component, inject, OnInit } from "@angular/core";
import { RewardMechanismTouchDeviceNotice } from "@components/reward-mechanism-touch-device-notice/reward-mechanism-touch-device-notice";
import { EducationLevelGroup } from "@domain/EducationLevel";
import { RewardCursor } from "@domain/User";
import { injectDialogData, RdxDialogCloseDirective, RdxDialogContentDirective, RdxDialogDescriptionDirective } from "@radix-ng/primitives/dialog";
import { AnalyticsService, RewardCursorSelectionSource } from "@services/analytics.service";
import { DeviceCapabilityService } from "@services/device-capability.service";
import { RewardService } from "@services/reward.service";

@Component({
  selector: "app-new-unblocked-reward-cursor-modal",
  imports: [RdxDialogCloseDirective, RdxDialogContentDirective, RdxDialogDescriptionDirective, RewardMechanismTouchDeviceNotice],
  template: `
    @let cursor = data.newRewardCursorUnlocked;
    @let deviceHasTouchCapabilities = deviceCapabilityService.hasTouchCapabilities();

    <div class="modal modal-medium" data-testid="new-unblocked-reward-cursor-modal" rdxDialogContent>
      <div class="modal-content">
        <h2 id="new-unblocked-reward-cursor-modal-title" class="title-h3 modal__title" rdxDialogTitle>Récompense débloquée!</h2>
        <div class="modal__subtitle">
          <img src="assets/cursors/new-cursor-stars-left.svg" class="subtitle__stars subtitle__stars-left" />
          <p id="new-unblocked-reward-cursor-modal-description" class="body-2 subtitle__content" rdxDialogDescription>
            Continue à t’entrainer pour débloquer<br />
            de nouveaux curseurs.
          </p>
          <img src="assets/cursors/new-cursor-stars-right.svg" class="subtitle__stars subtitle__stars-right" />
        </div>
        <div class="modal__cursors">
          <div class="modal__cursor cursor-and-arrow" [attr.data-testid]="'cursor-' + cursor">
            <img class="cursor__arrow" src="assets/cursors/cursor-arrow.svg" alt="Cursor Arrow" />
            <img
              [class]="'cursor__image cursor--' + cursor"
              [src]="'assets/cursors/' + cursor + '.svg'"
              [alt]="'Nouveau curseur débloqué: ' + cursor"
            />
          </div>
        </div>
      </div>
      <app-reward-mecanism-touch-device-notice />
      <div class="modal-actions">
        @if (deviceHasTouchCapabilities) {
          <button class="btn btn-secondary modal-action close-button" rdxDialogClose>Fermer</button>
        } @else {
          <button class="btn btn-secondary modal-action" (click)="handleKeepCurrentCursor()" rdxDialogClose>Garder mon curseur actuel</button>
          <button class="btn btn-primary modal-action" (click)="handleSelectNewUnblockedCursor()" rdxDialogClose>Utiliser le nouveau curseur</button>
        }
      </div>
    </div>
  `,
  styles: `
    @use "responsive" as *;

    app-dialog-title {
      display: block;
      width: 100%;
      margin-bottom: 4px;
    }

    .modal__title {
      display: block;
      width: 100%;
      text-align: center;
    }

    .modal__subtitle {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      width: 100%;
      margin-bottom: 32px;

      @include screenWidth("mobile-only") {
        flex-wrap: wrap;
      }
    }

    .subtitle__content {
      text-align: center;
      flex-shrink: 0;
      flex-grow: 0;

      @include screenWidth("mobile-only") {
        order: 1;
        width: 100%;
      }
    }

    .subtitle__stars {
      display: block;
      flex-shrink: 0;
      flex-grow: 0;
    }

    .subtitle__stars-left {
      width: 56px;
      height: 47px;

      @include screenWidth("mobile-only") {
        order: 2;
      }
    }

    .subtitle__stars-right {
      width: 53px;
      height: 47px;

      @include screenWidth("mobile-only") {
        order: 3;
      }
    }

    .modal__cursors {
      display: flex;
      justify-content: center;
      gap: 8px;
      width: 100%;
      padding: 20px 0;
      border-radius: 20px;
      border: 2px solid #e7e1d8;
      background: #f4fbfe;

      .cursor-and-arrow {
        @include screenWidth("mobile-only") {
          transform: scale(1.5);
        }
      }
    }

    .modal__cursor {
      position: relative;
      flex-grow: 0;
      flex-shrink: 0;
    }

    .close-button {
      padding: 18px 0px;
      flex: 1;

      @include screenWidth("to-tablet") {
        width: 100%;
      }
    }
  `,
})
export class NewUnblockedRewardCursorModal implements OnInit {
  protected deviceCapabilityService = inject(DeviceCapabilityService);
  private rewardService = inject(RewardService);
  private analyticsService = inject(AnalyticsService);

  readonly data = injectDialogData<{
    newRewardCursorUnlocked: RewardCursor;
    userCurrentCursor: RewardCursor | undefined;
    selectedEducationLevelGroup: EducationLevelGroup;
  }>();

  ngOnInit() {
    this.analyticsService.trackNewRewardCursorUnlocked(this.data.selectedEducationLevelGroup, this.data.newRewardCursorUnlocked);
  }

  protected handleSelectNewUnblockedCursor() {
    this.rewardService.selectNewUnblockedCursor();
    this.analyticsService.trackRewardCursorSelection(
      this.data.selectedEducationLevelGroup,
      this.data.newRewardCursorUnlocked,
      RewardCursorSelectionSource.UnlockedModal
    );
  }

  protected handleKeepCurrentCursor() {
    this.analyticsService.trackRewardCursorMaintained(this.data.selectedEducationLevelGroup, this.data.userCurrentCursor);
  }
}
